// eslint-disable-next-line
// @ts-nocheck
/*
We will migrate this entire file in the future. Currently we are overwriting palette in a non standard way.
In the future we will override the standard colors and disallow "normal" material UI colors and fonts
For text color see: https://github.com/mui/material-ui/issues/33140
see ticket: https://einwert.atlassian.net/browse/WEB-330
 */
import { createTheme } from '@mui/material/styles'
import { default as typography } from './typography'
import { default as Colors } from './colors'

import { deDE } from '@mui/material/locale'
import { tokens } from 'ComponentLibrary/style-dictionary/output'

declare module '@mui/material/Typography' {
    interface TypographyPropsVariantOverrides {
        headline1: true
        headline2: true
        headline3: true
        bodyTextLarge: true
        bodyTextMedium: true
        bodyTextSmall: true
        bodyTextXSmall: true
        result: true
        CaptionMedium: true
        captionSmall: true
        monetaryValueXLarge: true
        monetaryValueLarge: true
        monetaryValueMedium: true

        // Piazza Typography variants
        headline1: true
        headline2: true
        headline3: true
        headline4: true
        headline5: true
        textM: true
        textLinkM: true
        textS: true
        textLinkS: true
        textXS: true
        graphLabel: true
        link: true
        linkCaps: true
        valueL: true
        valueM: true
        valueS: true
    }
}

declare module '@mui/material/styles' {
    interface TypographyVariants {
        headline1: React.CSSProperties
        headline2: React.CSSProperties
        headline3: React.CSSProperties
        bodyTextLarge: React.CSSProperties
        bodyTextMedium: React.CSSProperties
        bodyTextSmall: React.CSSProperties
        bodyTextXSmall: React.CSSProperties
        result: React.CSSProperties
        captionMedium: React.CSSProperties
        captionSmall: React.CSSProperties
        monetaryValueXLarge: React.CSSProperties
        monetaryValueLarge: React.CSSProperties
        monetaryValueMedium: React.CSSProperties
    }

    // allow configuration using `createTheme`
    interface TypographyVariantsOptions {
        headline1: React.CSSProperties
        headline2: React.CSSProperties
        headline3: React.CSSProperties
        bodyTextLarge: React.CSSProperties
        bodyTextLargeBlack: React.CSSProperties
        bodyTextMedium: React.CSSProperties
        bodyTextSmall: React.CSSProperties
        bodyTextXSmall: React.CSSProperties
        result: React.CSSProperties
        captionMedium: React.CSSProperties
        captionSmall: React.CSSProperties
        monetaryValueXLarge: React.CSSProperties
        monetaryValueLarge: React.CSSProperties
        monetaryValueMedium: React.CSSProperties
    }

    interface Palette {
        primary1: typeof Colors.primary1
        primary2: typeof Colors.primary2
        secondary: typeof Colors.secondary
        secondary1: typeof Colors.secondary1
        customtext: typeof Colors.customtext
        green: typeof Colors.green
        white: typeof Colors.white
        marker: typeof Colors.marker
        danger: typeof Colors.danger
        chartColors: typeof Colors.chartColors
        chartColorsShaded: typeof Colors.chartColorsShaded
        status: typeof Colors.status
        taskStatus: typeof Colors.taskStatus
    }

    interface BreakpointOverrides {
        singleColumn: true
    }
}

let theme = createTheme({
    palette: Colors,
    typography: {
        fontWeightRegular: typography.fontWeightRegular,
        fontWeightMedium: typography.fontWeightMedium,
        fontFamily: typography.fontFamily,
        ...typography.fonts,
    },
    spacing: 4,
    breakpoints: {
        values: {
            xl: 2560,
            lg: 1920,
            md: 1366,
            sm: 1024,
            xs: 0,
            singleColumn: 1366,
        },
    },
})

theme = createTheme(theme, {
    components: {
        MuiTypography: {
            defaultProps: {
                // color: theme.palette.customtext.darkblue100,
            },
        },
        MuiIcon: {
            styleOverrides: {
                root: {
                    padding: '0.5rem',
                    color: theme.palette.primary1.blue100,
                },
            },
        },
        MuiIconButton: {
            defaultProps: {
                disableRipple: true,
            },
            styleOverrides: {
                root: {
                    color: tokens.ColorIconSubtle,
                    borderRadius: 0,
                    padding: '0.5rem',
                    '&:hover': {
                        color: tokens.ColorIconDefault,
                    },
                    '&.active': {
                        color: tokens.ColorIconDefault,
                    },
                },
            },
        },
        MuiDivider: {
            styleOverrides: {
                root: {
                    borderColor: theme.palette.secondary1.grey,
                    borderBottomWidth: '1px',
                    borderRightWidth: '1px',
                },
            },
        },
        MuiRating: {
            defaultProps: {
                precision: 0.1,
            },
        },
        MuiCircularProgress: {
            defaultProps: {
                size: '3rem',
                thickness: 2,
            },
            styleOverrides: {
                root: {
                    position: 'absolute',
                    left: 'calc(50% - 1.5rem)',
                    top: 'calc(50% - 1.5rem)',
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    '&.textGenerator': {
                        borderRadius: '6px',
                        padding: '1.25rem',
                        fontWeight: 400,
                        fontSize: '0.75rem',
                        '&.Mui-disabled': {
                            border: `2px solid ${theme.palette.secondary1.grey}`,
                            color: theme.palette.customtext.darkblue100,
                        },
                        '&.Mui-focused': {
                            border: `2px solid ${theme.palette.primary1.blue100}`,
                            color: theme.palette.customtext.darkblue100,
                        },
                        '& .Mui-disabled': {
                            WebkitTextFillColor: 'unset',
                        },
                    },
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .Mui-disabled > svg > :not(.Mui-Chip)': {
                        backgroundColor: tokens.ColorSurfaceDisabled,
                        zIndex: 1,
                    },
                    '& .Mui-disabled.MuiOutlinedInput-root': { backgroundColor: tokens.ColorSurfaceDisabled },
                    '& .Mui-disabled > .MuiOutlinedInput-notchedOutline': {
                        borderRadius: '4px',
                        borderColor: 'transparent!important',
                    },
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    '&.Mui-disabled': {
                        backgroundColor: tokens.ColorSurfaceDisabled,
                        border: 'none!important',
                    },
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    '&.clickable': {
                        borderRadius: '6px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        cursor: 'pointer',
                        gap: '0.5rem',
                    },
                },
            },
        },
        MuiTabs: {
            styleOverrides: {
                indicator: {
                    height: '1px',
                    backgroundColor: theme.palette.secondary.main,
                },
                flexContainer: {
                    gap: '1.875rem',
                    borderBottom: `1px solid ${theme.palette.secondary1.grey}`,
                },
                flexContainerVertical: {
                    paddingBottom: 0,
                    gap: '0.5rem',
                    '& .MuiTab-root': {
                        alignItems: 'start',
                        '& > span': {
                            paddingBottom: '8px',
                        },
                    },
                },
            },
        },
        MuiAutocomplete: {
            styleOverrides: {
                listbox: {
                    scrollbarWidth: 'thin',
                },
            },
        },
        MuiFormLabel: {
            styleOverrides: {
                root: {
                    color: tokens.ColorTextSubtle,
                },
            },
        },
        MuiTab: {
            defaultProps: {
                disableRipple: true,
            },
            styleOverrides: {
                root: {
                    minHeight: 0,
                    minWidth: 0,
                    textTransform: 'none',
                    color: theme.palette.customtext.black23,
                    '&.Mui-selected': {
                        color: theme.palette.secondary.main,
                    },
                },
            },
        },
    },
    deDE,
})

export default theme
